import { useState, useEffect, useCallback, useMemo } from "react";
import { WhatsAppSettings } from "./WhatsAppSettings";
import { EmailSettings } from "./EmailSettings";
import { AiSettings } from "./AiSettings";
import { SignatureSpending } from "./SignatureSpending";
import { communicationsApi } from "@services/api/organizations";
import { LineChart } from "@components/charts/LineChart";
import type { LineChartDataPoint } from "@components/charts/LineChart";

function fmtEur(v: number, decimals = 2): string {
  return v.toLocaleString("fr-FR", { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + "\u00A0\u20AC";
}

type CombinedDay = {
  date: string;
  whatsapp_count: number;
  whatsapp_cost: number;
  email_count: number;
  email_cost: number;
  signature_count: number;
  signature_cost: number;
  ai_count: number;
  ai_cost: number;
  sms_count?: number;
  sms_cost?: number;
  total_cost: number;
};

type Tab = "global" | "whatsapp" | "email" | "signatures" | "ia";

export function MessagerieSettings() {
  const [activeTab, setActiveTab] = useState<Tab>("global");
  // No top-up button here any more; the banner still greets the ?topup=success
  // callback of a payment link paid elsewhere.
  const [topupSuccess, setTopupSuccess] = useState(false);

  const [spending, setSpending] = useState<{
    solde: number;
    total_spent: number;
    whatsapp: { total_spent: number; transaction_count: number };
    email: { total_spent: number; transaction_count: number };
    signatures: { total_spent: number; transaction_count: number };
    ai: { total_spent: number; transaction_count: number };
    sms?: { total_spent: number; transaction_count: number };
  } | null>(null);
  const [combinedDaily, setCombinedDaily] = useState<CombinedDay[]>([]);

  const fetchCombined = useCallback(async () => {
    try {
      const [s, daily] = await Promise.all([
        communicationsApi.getSpending(),
        communicationsApi.getDailyStats(30),
      ]);
      setSpending(s);
      setCombinedDaily(daily);
    } catch {
      // API not available yet
    }
  }, []);

  useEffect(() => {
    fetchCombined();
  }, [fetchCombined]);

  // Detect topup=success from Bridge callback redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("topup") === "success") {
      setTopupSuccess(true);
      fetchCombined();
      // Clean the URL
      params.delete("topup");
      const clean = params.toString();
      const newUrl = window.location.pathname + (clean ? `?${clean}` : "");
      window.history.replaceState({}, "", newUrl);
      // Auto-dismiss after 5s
      setTimeout(() => setTopupSuccess(false), 5000);
    }
  }, []);

  const combinedCostChart: LineChartDataPoint[] = useMemo(
    () =>
      combinedDaily.map((d) => {
        const date = new Date(d.date);
        return {
          label: date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }).replace(".", ""),
          tooltipLabel: date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }),
          value: d.total_cost,
        };
      }),
    [combinedDaily]
  );

  const tabs: { key: Tab; label: string }[] = [
    { key: "global", label: "Vue globale" },
    { key: "whatsapp", label: "WhatsApp" },
    { key: "email", label: "Email" },
    { key: "signatures", label: "Signatures" },
    { key: "ia", label: "IA" },
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto">
        <div className="flex flex-col p-8 max-w-2xl">
          {/* Page title */}
          <h1
            className="text-hx font-semibold mb-6"
            style={{ fontFamily: "Poppins", color: "#111827" }}
          >
            Facturation
          </h1>

          {/* Tab bar */}
          <div
            className="flex flex-row gap-1 mb-8 p-1 rounded-xl"
            style={{ background: "#F3F4F6" }}
          >
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className="flex-1 py-2.5 px-4 rounded-lg text-h2 font-medium transition-all"
                style={{
                  fontFamily: "Poppins",
                  background: activeTab === tab.key ? "#FFFFFF" : "transparent",
                  color: activeTab === tab.key ? "#111827" : "#6B7280",
                  boxShadow: activeTab === tab.key ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                  cursor: "pointer",
                  border: "none",
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Topup success banner */}
          {topupSuccess && (
            <div
              className="flex flex-row items-center gap-3 p-4 rounded-xl mb-2"
              style={{
                background: "#F0FDF4",
                border: "1px solid #BBF7D0",
              }}
            >
              <div
                className="flex items-center justify-center flex-shrink-0"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 999,
                  background: "#22C55E",
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <div className="flex flex-col">
                <span
                  className="text-h2 font-semibold"
                  style={{ fontFamily: "Poppins", color: "#166534" }}
                >
                  Recharge effectuee
                </span>
                <span
                  className="text-h3"
                  style={{ fontFamily: "Poppins", color: "#6B7280" }}
                >
                  Votre solde sera mis a jour dans quelques instants.
                </span>
              </div>
              <button
                onClick={() => setTopupSuccess(false)}
                className="ml-auto"
                style={{ color: "#6B7280", cursor: "pointer", background: "none", border: "none", fontSize: 18 }}
              >
                x
              </button>
            </div>
          )}

          {/* Vue globale tab */}
          {activeTab === "global" && (
            <div className="flex flex-col gap-6">
              {spending && (
                <>
                  {/* Unified solde card */}
                  <div
                    className="flex flex-col p-6 rounded-xl"
                    style={{
                      background: spending.solde > 0 ? "#F0FDF4" : "#FEF2F2",
                      border: `1px solid ${spending.solde > 0 ? "#BBF7D0" : "#FECACA"}`,
                    }}
                  >
                    <span
                      className="text-h3 font-medium mb-1"
                      style={{ fontFamily: "Poppins", color: "#6B7280" }}
                    >
                      Solde
                    </span>
                    <span
                      className="text-hx font-bold"
                      style={{
                        fontFamily: "Poppins",
                        color: spending.solde > 0 ? "#166534" : "#991B1B",
                        fontSize: 28,
                      }}
                    >
                      {fmtEur(spending.solde)}
                    </span>
                  </div>

                  {/* Per-channel stats — 2 rows of 2 */}
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-row gap-3">
                      <div
                        className="flex flex-col flex-1 p-5 rounded-xl"
                        style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }}
                      >
                        <span
                          className="text-h3 font-medium mb-1"
                          style={{ fontFamily: "Poppins", color: "#6B7280" }}
                        >
                          WhatsApp
                        </span>
                        <span
                          className="text-hx font-bold"
                          style={{ fontFamily: "Poppins", color: "#166534" }}
                        >
                          {fmtEur(spending.whatsapp.total_spent)}
                        </span>
                        <span
                          className="text-h4 mt-1"
                          style={{ fontFamily: "Poppins", color: "#6B7280" }}
                        >
                          {spending.whatsapp.transaction_count} messages
                        </span>
                      </div>
                      <div
                        className="flex flex-col flex-1 p-5 rounded-xl"
                        style={{ background: "#EFF6FF", border: "1px solid #BFDBFE" }}
                      >
                        <span
                          className="text-h3 font-medium mb-1"
                          style={{ fontFamily: "Poppins", color: "#6B7280" }}
                        >
                          Email
                        </span>
                        <span
                          className="text-hx font-bold"
                          style={{ fontFamily: "Poppins", color: "#1E40AF" }}
                        >
                          {fmtEur(spending.email.total_spent)}
                        </span>
                        <span
                          className="text-h4 mt-1"
                          style={{ fontFamily: "Poppins", color: "#6B7280" }}
                        >
                          {spending.email.transaction_count} emails
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-row gap-3">
                      <div
                        className="flex flex-col flex-1 p-5 rounded-xl"
                        style={{ background: "#FDF4FF", border: "1px solid #E9D5FF" }}
                      >
                        <span
                          className="text-h3 font-medium mb-1"
                          style={{ fontFamily: "Poppins", color: "#6B7280" }}
                        >
                          Signatures
                        </span>
                        <span
                          className="text-hx font-bold"
                          style={{ fontFamily: "Poppins", color: "#7C3AED" }}
                        >
                          {fmtEur(spending.signatures.total_spent)}
                        </span>
                        <span
                          className="text-h4 mt-1"
                          style={{ fontFamily: "Poppins", color: "#6B7280" }}
                        >
                          {spending.signatures.transaction_count} signatures
                        </span>
                      </div>
                      <div
                        className="flex flex-col flex-1 p-5 rounded-xl"
                        style={{ background: "#FFF7ED", border: "1px solid #FED7AA" }}
                      >
                        <span
                          className="text-h3 font-medium mb-1"
                          style={{ fontFamily: "Poppins", color: "#6B7280" }}
                        >
                          IA
                        </span>
                        <span
                          className="text-hx font-bold"
                          style={{ fontFamily: "Poppins", color: "#C2410C" }}
                        >
                          {fmtEur(spending.ai.total_spent)}
                        </span>
                        <span
                          className="text-h4 mt-1"
                          style={{ fontFamily: "Poppins", color: "#6B7280" }}
                        >
                          {spending.ai.transaction_count} appels
                        </span>
                      </div>
                      <div
                        className="flex flex-col flex-1 p-5 rounded-xl"
                        style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }}
                      >
                        <span
                          className="text-h3 font-medium mb-1"
                          style={{ fontFamily: "Poppins", color: "#6B7280" }}
                        >
                          SMS
                        </span>
                        <span
                          className="text-hx font-bold"
                          style={{ fontFamily: "Poppins", color: "#15803D" }}
                        >
                          {fmtEur(spending.sms?.total_spent || 0)}
                        </span>
                        <span
                          className="text-h4 mt-1"
                          style={{ fontFamily: "Poppins", color: "#6B7280" }}
                        >
                          {spending.sms?.transaction_count || 0} SMS
                        </span>
                      </div>
                    </div>
                    <div
                      className="flex flex-col p-5 rounded-xl"
                      style={{ background: "#F9FAFB", border: "1px solid #E5E7EB" }}
                    >
                      <span
                        className="text-h3 font-medium mb-1"
                        style={{ fontFamily: "Poppins", color: "#6B7280" }}
                      >
                        Total depense
                      </span>
                      <span
                        className="text-hx font-bold"
                        style={{ fontFamily: "Poppins", color: "#111827" }}
                      >
                        {fmtEur(spending.total_spent)}
                      </span>
                    </div>
                  </div>

                  {/* Combined spending chart */}
                  {combinedDaily.length > 0 && (
                    <div
                      className="p-5 rounded-xl"
                      style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}
                    >
                      <LineChart
                        data={combinedCostChart}
                        title="Depenses totales (30 jours)"
                        valuePrefix=""
                        valueSuffix=""
                        color="#8B5CF6"
                        emptyMessage="Aucune depense sur cette periode"
                        formatValue={(v) => fmtEur(v, 4)}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* WhatsApp tab */}
          {activeTab === "whatsapp" && (
            <WhatsAppSettings hideSoldeRecharge />
          )}

          {/* Email tab */}
          {activeTab === "email" && (
            <EmailSettings hideSoldeRecharge />
          )}

          {/* Signatures tab */}
          {activeTab === "signatures" && (
            <SignatureSpending hideSoldeRecharge />
          )}

          {/* IA tab */}
          {activeTab === "ia" && (
            <AiSettings hideSoldeRecharge />
          )}
        </div>
      </div>
    </div>
  );
}
